import { Ability, AbilityBuilder } from '@casl/ability';
import customerDataRequest from '../services/api/customer-data';

export const loadAbilityForUser = (user, can) => {
    if (user) {
        const {
            rolesList = [],
        } = user || {};

        rolesList.forEach((permission) => {
            const [action, item] = permission.split(':');
            can(action, item);
        });
    }
};

/**
 * Load listing abilities for the given user. This was added as a separate function because of the
 * promises. The app needed a response immediately about whether or not a user had permissions when
 * generating the menu, etc. We could make this more direct by including the listings a user owns
 * in the identity claims. For now, the ownership APIs remain the source of truth.
 *
 * @param user
 * @param token
 * @param ability
 * @return {Promise<*>}
 */
export const loadListingAbilitiesForUser = async (user, token, ability) => {
    if (!user) {
        return ability;
    }

    const { can, rules } = new AbilityBuilder(Ability);
    loadAbilityForUser(user, can); // ability.update replaces existing rules, so we need to load it in again

    if (ability.can('onlinedir', 'admin')) {
        // if a user can administrate listings, they can update any listing
        can('admin', 'claimed');
        can('admin', 'listing');
    } else {
        const ownershipPromises = [
            customerDataRequest('get', '/organizations/owners', token),
            customerDataRequest('get', '/sponsors/owners', token),
        ];

        await Promise.all(ownershipPromises).then((response) => {
            response[0].data.data.forEach((organizationOwnership) => {
                can('admin', 'claimed', { id: { $eq: organizationOwnership.organization_salesforce_id } });
            });
            response[1].data.data.forEach((sponsorOwnership) => {
                can('admin', 'claimed', { id: { $eq: sponsorOwnership.sponsor_advertiser_id } });
            });
        });
    }

    ability.update(rules);

    return ability;
};

export const defineAbility = (user) => {
    const { rules, can } = new AbilityBuilder(Ability);
    loadAbilityForUser(user, can);

    return new Ability(rules);
};

export const canViewManage = (ability) => ability.can('onlinedir', 'admin');

export const getAuthToken = (getAccessToken) => getAccessToken()
    .then((token) => token)
    .catch(() => null);
